import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { enqueueSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  FormLabel,
  FormGroup,
  Checkbox,
  FormControlLabel,
  RadioGroup,
  Radio,
  Button,
  CircularProgress,
} from '@mui/material';

const ShowForm = () => {
  const { id } = useParams();
  const [workflow, setWorkflow] = useState(null);
  const [form, setForm] = useState({});
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const token = localStorage.getItem('token');
  const { userId } = jwtDecode(token);

  useEffect(() => {
    const fetchWorkflow = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/forms/${id}`);
        console.log(response.data)

        setWorkflow(response.data.workflow);
        setForm(response.data);

        // initialize formData state
        const initialFormData = response.data.fields.reduce((acc, field) => {
          acc[field.fieldName] = field.fieldType === 'checkbox' ? [] : '';
          return acc;
        }, {});
        setFormData(initialFormData);

        setLoading(false);
      } catch (err) {
        setError('Failed to load workflow or form');
        setLoading(false);
        enqueueSnackbar('Failed to load form', { variant: 'error' });
      }
    };

    fetchWorkflow();
  }, [id]);

  const handleChange = (e, field) => {
    const { name, value, type, checked } = e.target;

    setFormData((prevData) => {
      if (type === 'checkbox') {
        const options = prevData[name] || [];
        if (checked && !options.includes(value)) {
          return { ...prevData, [name]: [...options, value] };
        } else if (!checked) {
          return { ...prevData, [name]: options.filter((v) => v !== value) };
        }
        return prevData;
      }

      return { ...prevData, [name]: value };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const checkboxField = form.fields.find((field) => field.fieldType === 'checkbox');
    if (checkboxField) {
      const isAtLeastOneChecked = formData[checkboxField.fieldName]?.length > 0;
      if (!isAtLeastOneChecked) {
        enqueueSnackbar(`Please select at least one option for ${checkboxField.fieldName.replace(/_/g, ' ')}`, { variant: 'warning' });
        return;
      }
    }

    const formattedFormData = Object.entries(formData).map(([fieldName, value]) => ({
      fieldName,
      value: Array.isArray(value) ? value : [value],
    }));

    const startNode = workflow.nodes.find((node) => node.id === workflow.startEntity);
  
    if (!startNode) {
      enqueueSnackbar('Start entity not found in workflow nodes', { variant: 'error' });
      return;
    }
    
    const currentApprover = startNode.outgoingEdges?.[0];
    
    if (!currentApprover) {
      enqueueSnackbar('No outgoing edge found from start entity', { variant: 'error' });
      return;
    }

    const activeWorkflowData = {
      formData: formattedFormData,
      nodes: workflow.nodes.map((node) => ({
        ...node,
        status: 'pending',
        actedBy: null,
        comments: ''
      })),
      finalApprovers: workflow.finalEntities,
      currentApprover,
      overallStatus: 'pending',
      filledBy: userId,
    };

    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/active-workflows/create`, activeWorkflowData);
      enqueueSnackbar('Form submitted successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Failed to submit form', { variant: 'error' });
      console.error('Error submitting form:', error);
    }
  };

  if (loading) return <CircularProgress />;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', p: 3, bgcolor: 'background.paper', boxShadow: 3, borderRadius: 2 }}>
      <Typography variant="h5" gutterBottom align='center'>
        {form?.title}
      </Typography>

      <form onSubmit={handleSubmit}>
        {form.fields ? (
          form.fields.map((field) => (
            <FormControl key={field.fieldName} fullWidth sx={{ mb: 2 }}>
              <FormLabel component="legend" sx={{ textTransform: 'capitalize' }}>
                {field.fieldName.replace(/_/g, ' ')}
              </FormLabel>

              {field.fieldType === 'text' && (
                <TextField
                  name={field.fieldName}
                  value={formData[field.fieldName]}
                  onChange={(e) => handleChange(e, field)}
                  required
                  variant="outlined"
                  fullWidth
                />
              )}

              {field.fieldType === 'text-area' && (
                <TextField
                  name={field.fieldName}
                  value={formData[field.fieldName]}
                  onChange={(e) => handleChange(e, field)}
                  required
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={4} 
                />
              )}

              {field.fieldType === 'number' && (
                <TextField
                  type="number"
                  name={field.fieldName}
                  value={formData[field.fieldName]}
                  onChange={(e) => handleChange(e, field)}
                  required
                  variant="outlined"
                  fullWidth
                />
              )}

              {field.fieldType === 'email' && (
                <TextField
                  type="email"
                  name={field.fieldName}
                  value={formData[field.fieldName]}
                  onChange={(e) => handleChange(e, field)}
                  required
                  variant="outlined"
                  fullWidth
                />
              )}

              {field.fieldType === 'date' && (
                <TextField
                  type="date"
                  name={field.fieldName}
                  value={formData[field.fieldName]}
                  onChange={(e) => handleChange(e, field)}
                  required
                  variant="outlined"
                  fullWidth
                />
              )}

              {field.fieldType === 'dropdown' && (
                <TextField
                  select
                  name={field.fieldName}
                  value={formData[field.fieldName]}
                  onChange={(e) => handleChange(e, field)}
                  required
                  variant="outlined"
                  fullWidth
                  SelectProps={{
                    native: true,
                  }}
                >
                  <option value="">Select</option>
                  {field.options.map((option) => (
                    <option key={option.label} value={option.label}>
                      {option.label}
                    </option>
                  ))}
                </TextField>
              )}

              {field.fieldType === 'checkbox' && (
                <FormGroup>
                  {field.options.map((option) => (
                    <FormControlLabel
                      key={option.label}
                      control={
                        <Checkbox
                          name={field.fieldName}
                          value={option.label}
                          checked={formData[field.fieldName]?.includes(option.label)}
                          onChange={(e) => handleChange(e, field)}
                        />
                      }
                      label={option.label}
                    />
                  ))}
                </FormGroup>
              )}

              {field.fieldType === 'radio' && (
                <RadioGroup
                  name={field.fieldName}
                  value={formData[field.fieldName]}
                  onChange={(e) => handleChange(e, field)}
                >
                  {field.options.map((option) => (
                    <FormControlLabel
                      key={option.label}
                      value={option.label}
                      control={<Radio />}
                      label={option.label}
                      required
                    />
                  ))}
                </RadioGroup>
              )}
            </FormControl>
          ))
        ) : (
          <Typography>No fields available for this form.</Typography>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
          >
            Submit
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default ShowForm;
